<script setup>
import { useAbility } from '@casl/vue'
import { buildSelfOrderUrl } from '@/utils/selfOrderUrl'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Table',
  },
})

const router = useRouter()
const ability = useAbility()
const tablesStore = useTablesStore()
const { loading, request } = useApiRequest()

// ── Filter ─────────────────────────────────────────────────────────────
const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Tersedia', value: 'available' },
  { title: 'Terisi', value: 'occupied' },
  { title: 'Reservasi', value: 'reserved' },
  { title: 'Gabungan', value: 'merged' },
]

const fetchTables = () => tablesStore.fetchTables()

watch(() => tablesStore.filters, fetchTables, { deep: true })

// ── Status visual ──────────────────────────────────────────────────────
const statusColor = status => ({
  available: 'success',
  occupied: 'warning',
  reserved: 'info',
  merged: 'secondary',
}[status] ?? 'grey')

const statusLabel = status => ({
  available: 'Tersedia',
  occupied: 'Terisi',
  reserved: 'Reservasi',
  merged: 'Gabungan',
}[status] ?? status)

// ── Mode pilih untuk gabung meja ─────────────────────────────────────────
const mergeMode = ref(false)
const selectedTableIds = ref([])

const selectedTables = computed(() =>
  tablesStore.tables.filter(t => selectedTableIds.value.includes(t.id))
)

const toggleMergeMode = () => {
  mergeMode.value = !mergeMode.value
  selectedTableIds.value = []
}

const toggleSelectTable = table => {
  if (!mergeMode.value) return
  // Hanya meja available atau occupied (bukan merged) yang bisa ikut digabung
  if (!['available', 'occupied'].includes(table.status)) return

  const idx = selectedTableIds.value.indexOf(table.id)
  if (idx === -1) selectedTableIds.value.push(table.id)
  else selectedTableIds.value.splice(idx, 1)
}

// ── Dialog states ──────────────────────────────────────────────────────
const formDialog = ref(false)
const editingTable = ref(null)
const openSessionDialog = ref(false)
const targetTable = ref(null)
const mergeDialog = ref(false)
const sessionInfoDialog = ref(false)
const activeSessionTable = ref(null)
const closeSessionConfirm = ref(false)
const qrDialog = ref(false)
const qrDialogTable = ref(null)
const deleteConfirm = ref(false)
const deletingTable = ref(null)
const copiedTableId = ref(null)

// ── Aksi kartu meja ──────────────────────────────────────────────────────
const handleCardClick = table => {
  if (mergeMode.value) {
    toggleSelectTable(table)
    return
  }

  if (table.status === 'available') {
    targetTable.value = table
    openSessionDialog.value = true
  } else if (table.status === 'occupied' || table.status === 'merged') {
    activeSessionTable.value = table
    sessionInfoDialog.value = true
  }
}

const handleSessionOpened = () => {
  fetchTables()
  // Langsung arahkan kasir ke POS dengan konteks meja terpilih
  router.push({
    name: 'pos',
    query: {
      table_id: targetTable.value.id,
      table_session_id: targetTable.value.current_table_session_id,
    },
  })
}

const goToPos = table => {
  router.push({
    name: 'pos',
    query: {
      table_id: table.id,
      table_session_id: table.current_table_session_id,
    },
  })
}

const handleMerged = () => {
  fetchTables()
  mergeMode.value = false
  selectedTableIds.value = []
}

const confirmCloseSession = async () => {
  await request(async () => {
    await tablesStore.closeSession(activeSessionTable.value.current_table_session_id)
    closeSessionConfirm.value = false
    sessionInfoDialog.value = false
    await fetchTables()
  })
}

const openEdit = table => {
  editingTable.value = table
  formDialog.value = true
}

const openCreate = () => {
  editingTable.value = null
  formDialog.value = true
}

const confirmDelete = table => {
  deletingTable.value = table
  deleteConfirm.value = true
}

const handleDelete = async () => {
  await request(async () => {
    await tablesStore.deleteTable(deletingTable.value.id)
    deleteConfirm.value = false
    await fetchTables()
  })
}

const showQrCode = table => {
  qrDialogTable.value = table
  qrDialog.value = true
}

const quickCopyLink = async table => {
  try {
    await navigator.clipboard.writeText(buildSelfOrderUrl(table.qr_token))
    copiedTableId.value = table.id
    setTimeout(() => {
      if (copiedTableId.value === table.id) copiedTableId.value = null
    }, 2000)
  } catch (err) {
    console.error('[Tables] Gagal copy link:', err)
  }
}

onMounted(fetchTables)
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12" class="d-flex align-center flex-wrap gap-3">
        <div>
          <h4 class="text-h4 font-weight-medium mb-1">Denah Meja</h4>
          <p class="text-body-2 text-medium-emphasis mb-0">
            Kelola meja, sesi, dan gabung/pindah meja
          </p>
        </div>

        <VSpacer />

        <VBtn
          :color="mergeMode ? 'error' : 'secondary'"
          variant="tonal"
          :prepend-icon="mergeMode ? 'ri-close-line' : 'ri-git-merge-line'"
          @click="toggleMergeMode"
        >
          {{ mergeMode ? 'Batal Gabung' : 'Gabung Meja' }}
        </VBtn>

        <VBtn
          v-if="ability.can('create', 'Table')"
          color="primary"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Tambah Meja
        </VBtn>
      </VCol>
    </VRow>

    <!-- Filter -->
    <VRow class="mb-2">
      <VCol cols="12" sm="4" md="3">
        <VTextField
          v-model="tablesStore.filters.area_name"
          label="Filter Area"
          placeholder="Mis. Indoor"
          density="compact"
          clearable
          hide-details
        />
      </VCol>
      <VCol cols="12" sm="4" md="3">
        <VSelect
          v-model="tablesStore.filters.status"
          :items="statusOptions"
          label="Filter Status"
          density="compact"
          hide-details
        />
      </VCol>
    </VRow>

    <!-- Banner mode gabung -->
    <VAlert v-if="mergeMode" type="info" variant="tonal" class="mb-4">
      Klik meja yang ingin digabung (minimal 2 meja berstatus Tersedia/Terisi).
      Terpilih: {{ selectedTableIds.length }} meja.
    </VAlert>

    <!-- Loading -->
    <div v-if="loading && tablesStore.tables.length === 0" class="d-flex justify-center pa-8">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <!-- Empty -->
    <div v-else-if="tablesStore.tables.length === 0" class="text-center pa-8">
      <VIcon icon="ri-table-line" size="64" color="grey-lighten-1" />
      <p class="text-body-1 mt-4">Belum ada meja</p>
      <VBtn v-if="ability.can('create', 'Table')" color="primary" class="mt-2" @click="openCreate">
        Tambah Meja Pertama
      </VBtn>
    </div>

    <!-- Grid meja -->
    <VRow v-else dense>
      <VCol
        v-for="table in tablesStore.tables"
        :key="table.id"
        cols="6"
        sm="4"
        md="3"
        lg="2"
      >
        <VCard
          :class="[
            'table-card',
            `border-${statusColor(table.status)}`,
            { 'table-card--selected': selectedTableIds.includes(table.id) },
          ]"
          :variant="selectedTableIds.includes(table.id) ? 'tonal' : 'outlined'"
          :color="selectedTableIds.includes(table.id) ? 'primary' : undefined"
          @click="handleCardClick(table)"
        >
          <VCardText class="text-center pa-3">
            <VIcon icon="ri-table-2" size="28" :color="statusColor(table.status)" class="mb-1" />
            <div class="text-subtitle-1 font-weight-bold">{{ table.code }}</div>
            <div v-if="table.name" class="text-caption text-medium-emphasis text-truncate">
              {{ table.name }}
            </div>
            <div class="text-caption text-medium-emphasis">
              <VIcon icon="ri-group-line" size="12" /> {{ table.capacity }}
              <span v-if="table.area_name"> · {{ table.area_name }}</span>
            </div>
            <VChip :color="statusColor(table.status)" size="x-small" class="mt-2">
              {{ statusLabel(table.status) }}
            </VChip>
          </VCardText>

          <div v-if="!mergeMode" class="table-card__actions">
            <VBtn icon size="x-small" variant="text" @click.stop="showQrCode(table)">
              <VIcon icon="ri-qr-code-line" size="16" />
              <VTooltip activator="parent">Lihat QR Self-Order</VTooltip>
            </VBtn>
            <VBtn icon size="x-small" variant="text" @click.stop="quickCopyLink(table)">
              <VIcon
                :icon="copiedTableId === table.id ? 'ri-check-line' : 'ri-link'"
                :color="copiedTableId === table.id ? 'success' : undefined"
                size="16"
              />
              <VTooltip activator="parent">
                {{ copiedTableId === table.id ? 'Tersalin!' : 'Copy Link Self-Order' }}
              </VTooltip>
            </VBtn>
            <VBtn
              v-if="ability.can('update', 'Table')"
              icon
              size="x-small"
              variant="text"
              @click.stop="openEdit(table)"
            >
              <VIcon icon="ri-edit-line" size="16" />
            </VBtn>
            <VBtn
              v-if="ability.can('delete', 'Table') && table.status === 'available'"
              icon
              size="x-small"
              variant="text"
              color="error"
              @click.stop="confirmDelete(table)"
            >
              <VIcon icon="ri-delete-bin-line" size="16" />
            </VBtn>
          </div>
        </VCard>
      </VCol>
    </VRow>

    <!-- Floating action bar mode gabung -->
    <VSlideYTransition>
      <div v-if="mergeMode && selectedTableIds.length >= 2" class="merge-fab">
        <VCard color="primary" theme="dark">
          <VCardText class="d-flex align-center gap-3">
            <span>{{ selectedTableIds.length }} meja dipilih</span>
            <VBtn color="white" variant="flat" @click="mergeDialog = true">
              Lanjut Gabungkan
            </VBtn>
          </VCardText>
        </VCard>
      </div>
    </VSlideYTransition>

    <!-- Dialogs -->
    <TableFormDialog
      v-model="formDialog"
      :table="editingTable"
      @saved="fetchTables"
    />

    <OpenSessionDialog
      v-model="openSessionDialog"
      :table="targetTable"
      @opened="handleSessionOpened"
    />

    <MergeTablesDialog
      v-model="mergeDialog"
      :selected-tables="selectedTables"
      @merged="handleMerged"
    />

    <ConfirmDialog
      v-model="deleteConfirm"
      title="Hapus Meja?"
      :message="`Meja ${deletingTable?.code} akan dihapus permanen.`"
      confirm-text="Hapus"
      color="error"
      :loading="loading"
      @confirm="handleDelete"
    />

    <!-- Info sesi meja aktif -->
    <VDialog v-model="sessionInfoDialog" max-width="480">
      <VCard v-if="activeSessionTable">
        <VCardTitle class="text-h6">
          Sesi Meja — {{ activeSessionTable.code }}
        </VCardTitle>
        <VCardText>
          <p class="mb-1">
            <strong>Status:</strong>
            <VChip :color="statusColor(activeSessionTable.status)" size="small" class="ml-2">
              {{ statusLabel(activeSessionTable.status) }}
            </VChip>
          </p>
          <p v-if="activeSessionTable.current_table_session" class="mb-1">
            <strong>Kode Sesi:</strong> {{ activeSessionTable.current_table_session.session_code }}
          </p>
          <p v-if="activeSessionTable.current_table_session?.pax" class="mb-1">
            <strong>Jumlah Tamu:</strong> {{ activeSessionTable.current_table_session.pax }}
          </p>
          <p
            v-if="activeSessionTable.current_table_session?.tables?.length > 1"
            class="mb-1"
          >
            <strong>Meja Bergabung:</strong>
            {{ activeSessionTable.current_table_session.tables.map(t => t.code).join(', ') }}
          </p>
        </VCardText>
        <VCardActions class="flex-wrap">
          <VBtn
            v-if="ability.can('create', 'Sales')"
            color="primary"
            prepend-icon="ri-add-shopping-cart-line"
            @click="goToPos(activeSessionTable)"
          >
            Tambah Pesanan
          </VBtn>
          <VBtn
            color="error"
            variant="tonal"
            prepend-icon="ri-lock-line"
            @click="closeSessionConfirm = true"
          >
            Tutup Sesi
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model="closeSessionConfirm"
      title="Tutup Sesi Meja?"
      message="Sesi hanya bisa ditutup jika semua order & split bill sudah lunas."
      confirm-text="Ya, Tutup Sesi"
      color="error"
      :loading="loading"
      @confirm="confirmCloseSession"
    />

    <!-- QR Code Self-Order -->
    <TableQrDialog
      v-model="qrDialog"
      :table="qrDialogTable"
    />
  </div>
</template>

<style scoped>
.table-card {
  cursor: pointer;
  position: relative;
  transition: transform 0.15s ease;
}

.table-card:hover {
  transform: translateY(-2px);
}

.table-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}

.table-card__actions {
  position: absolute;
  top: 2px;
  right: 2px;
  display: flex;
  background: rgba(var(--v-theme-surface), 0.9);
  border-radius: 4px;
}

.merge-fab {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  border-radius: 8px;
  overflow: hidden;
}
</style>
